/**
 * Helpers for the Stripe-onboarding nudges that key off online-payment slots.
 *
 * Two slot shapes flow through the app and both are accepted here:
 *   • raw API slots — carry `acceptsOnline` + `anchorDate`
 *   • UI-mapped slots (`mapApiSlot`) — carry `paymentMethods` + `startDate`
 */
import { isSlotInPast } from './slotUtils';

type PaymentMethod = 'cash' | 'online';

/** Minimal shape of a slot rule (recurring rule or one-off), in either slot shape. */
interface SlotRule {
  acceptsOnline?: boolean | null;
  paymentMethods?: PaymentMethod[];
  repeat?: string;
  /** Raw API one-off/recurrence start date (YYYY-MM-DD). */
  anchorDate?: string;
  /** UI-mapped equivalent of `anchorDate`. */
  startDate?: string;
  repeatUntil?: string | null;
  endTime?: number;
  [key: string]: any;
}

interface ActivityWithSlots {
  slots?: SlotRule[] | null;
  [key: string]: any;
}

/** Online acceptance across both slot shapes. */
function slotAcceptsOnline(slot: SlotRule): boolean {
  return slot.acceptsOnline === true || slot.paymentMethods?.includes('online') === true;
}

/**
 * Whether a slot rule still has an occurrence today or in the future.
 * Evaluated at the rule level — no occurrence expansion:
 *   • one-off (repeat 'NONE'): upcoming until its own date/endTime passes.
 *   • recurring, open-ended (no repeatUntil): always upcoming.
 *   • recurring with repeatUntil: upcoming until that end date/endTime passes.
 */
export function isSlotRuleUpcoming(slot: SlotRule): boolean {
  const endTime = typeof slot.endTime === 'number' ? slot.endTime : 1440;
  const startDate = slot.anchorDate ?? slot.startDate;
  const isRecurring = !!slot.repeat && slot.repeat !== 'NONE';

  if (isRecurring) {
    if (!slot.repeatUntil) return true;
    return !isSlotInPast({ date: slot.repeatUntil, endTime });
  }

  if (!startDate) return false;
  return !isSlotInPast({ date: startDate, endTime });
}

/**
 * Whether any slot in the array is an *upcoming* slot accepting online payment.
 * Purely-past online slots are excluded: they can no longer take an online
 * booking, so they must not drive an onboarding nudge.
 */
export function hasUpcomingOnlineSlot(slots?: SlotRule[] | null): boolean {
  if (!slots) return false;
  return slots.some((slot) => slotAcceptsOnline(slot) && isSlotRuleUpcoming(slot));
}

/**
 * Whether the professional has at least one upcoming online-payment slot across
 * all their activities. Single source of truth for the Stripe-onboarding nudges
 * (agenda banner, settings payments pill, nav badge, per-activity notices).
 */
export function hasUpcomingOnlinePaymentSlot(activities?: ActivityWithSlots[] | null): boolean {
  if (!activities) return false;
  return activities.some((activity) => hasUpcomingOnlineSlot(activity.slots));
}
